import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import {
  Button,
  CardActions,
  Stack,
  Card,
  CardContent,
  Typography,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  Snackbar,
  Collapse,
  IconButton,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import PricelistSelect from "../pricing/PricelistSelect";
import { API, graphqlOperation } from "aws-amplify";
import { updateUser } from "../../graphql/mutations";

const UserCard = ({ user, onClick, PrimaryAction, onPricelistUpdate }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [showFeedback, setShowFeedback] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showOffsetControls, setShowOffsetControls] = useState(false);

  const formik = useFormik({
    initialValues: {
      providerOffsetType: user.providerOffsetType || "percentage",
      providerOffsetValue: user.providerOffsetValue?.toString() || "",
    },
    onSubmit: async (values) => {
      try {
        const input = {
          id: user.id,
          providerOffsetType: values.providerOffsetType,
          providerOffsetValue: values.providerOffsetValue
            ? parseFloat(values.providerOffsetValue)
            : null,
        };

        await API.graphql(graphqlOperation(updateUser, { input }));
        setSnackbarMessage("Provider offset updated successfully!");
        setShowFeedback(true);
        if (onPricelistUpdate) {
          onPricelistUpdate();
        }
      } catch (error) {
        console.error("Error updating provider offset:", error);
        setSnackbarMessage("Error updating provider offset");
        setShowFeedback(true);
      }
    },
  });

  // Update form when user changes
  useEffect(() => {
    if (user) {
      formik.setValues({
        providerOffsetType: user.providerOffsetType || "percentage",
        providerOffsetValue: user.providerOffsetValue?.toString() || "",
      });
    }
  }, [user]);

  return (
    <div style={{ margin: "10px", fontWeight: "bold" }}>
      <Stack>
        <Card
          sx={{
            minWidth: 275,
            backgroundColor: "#000000",
            borderRadius: "5px",
            borderColor: theme.palette.primary.main,
            borderWidth: "2px",
            borderStyle: "solid",
          }}
        >
          <CardContent>
            {/* Username and Email at Top - Prominent */}
            <Typography
              sx={{ fontSize: 18, fontWeight: "bold", color: "white" }}
              gutterBottom
            >
              {user.username || user.email}
            </Typography>
            {user.email && (
              <Typography
                sx={{ fontSize: 16, fontWeight: "bold", color: "#ccc" }}
                gutterBottom
              >
                {user.email}
              </Typography>
            )}

            {/* Customer's Personal Offset Badge */}
            <div style={{ marginBottom: "10px" }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                gutterBottom
              >
                Customer's Personal Offset:
              </Typography>
              <Chip
                label={
                  user.priceOffsetValue && user.priceOffsetValue !== 0
                    ? user.priceOffsetType === "percentage"
                      ? `${user.priceOffsetValue > 0 ? "+" : ""}${
                          user.priceOffsetValue
                        }%`
                      : `$${user.priceOffsetValue > 0 ? "+" : ""}${Math.abs(
                          user.priceOffsetValue,
                        ).toFixed(2)}`
                    : "No personal offset"
                }
                size="small"
                color={
                  user.priceOffsetValue && user.priceOffsetValue > 0
                    ? "warning"
                    : user.priceOffsetValue && user.priceOffsetValue < 0
                      ? "success"
                      : "default"
                }
                sx={{ fontWeight: "bold", mb: 1 }}
              />
            </div>

            {/* Provider's Personal Offset Badge */}
            <div style={{ marginBottom: "10px" }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                gutterBottom
              >
                Provider's Personal Offset:
              </Typography>
              <Chip
                label={
                  user.providerOffsetValue && user.providerOffsetValue !== 0
                    ? user.providerOffsetType === "percentage"
                      ? `${user.providerOffsetValue > 0 ? "+" : ""}${
                          user.providerOffsetValue
                        }%`
                      : `$${user.providerOffsetValue > 0 ? "+" : ""}${Math.abs(
                          user.providerOffsetValue,
                        ).toFixed(2)}`
                    : "No personal offset"
                }
                size="small"
                color={
                  user.providerOffsetValue && user.providerOffsetValue > 0
                    ? "warning"
                    : user.providerOffsetValue && user.providerOffsetValue < 0
                      ? "success"
                      : "default"
                }
                sx={{ fontWeight: "bold", mb: 1 }}
              />
            </div>
            <Typography
              sx={{ fontSize: 16, fontWeight: "bold", color: "white" }}
              gutterBottom
            >
              {user.companyName}
            </Typography>

            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {user.address}
            </Typography>
            {user.city && (
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {user.city}, {user.state} {user.zip}
              </Typography>
            )}
            <Typography
              sx={{
                marginTop: "15px",
                marginBottom: "15px",
                fontWeight: "bold",
              }}
              variant="body2"
            >
              {user.cellNumber}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {user.contactName}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {user.email}
            </Typography>
            {user.activePricelist && (
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Active Pricelist: {user.activePricelist.name}
              </Typography>
            )}
            <div style={{ marginTop: "15px" }}>
              <PricelistSelect
                pricelistID={user.activePricelistID}
                userID={user.id}
                providerID={user.createdByUserId}
              />
            </div>
          </CardContent>
          <CardActions sx={{ justifyContent: "space-between" }}>
            <Button
              size="small"
              onClick={() => navigate(`/user-details/${user.username}`)}
            >
              View Details
            </Button>

            <IconButton
              size="small"
              onClick={() => setShowOffsetControls(!showOffsetControls)}
              sx={{ color: "white" }}
            >
              {showOffsetControls ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              <Typography variant="caption" sx={{ ml: 0.5, color: "white" }}>
                {showOffsetControls ? "Hide" : "Show"} Offset Controls
              </Typography>
            </IconButton>

            {PrimaryAction && <PrimaryAction />}
          </CardActions>

          <Collapse in={showOffsetControls} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, borderTop: "1px solid rgba(0,0,0,0.12)" }}>
              <Typography variant="subtitle2" gutterBottom>
                Provider Offset Controls
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Offset Type</InputLabel>
                    <Select
                      label="Offset Type"
                      name="providerOffsetType"
                      value={formik.values.providerOffsetType}
                      onChange={formik.handleChange}
                    >
                      <MenuItem value="percentage">Percentage (%)</MenuItem>
                      <MenuItem value="fixed">Fixed Amount ($)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={
                      formik.values.providerOffsetType === "percentage"
                        ? "Percentage Offset"
                        : "Fixed Amount Offset"
                    }
                    name="providerOffsetValue"
                    value={formik.values.providerOffsetValue}
                    onChange={formik.handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {formik.values.providerOffsetType === "percentage"
                            ? "%"
                            : "$"}
                        </InputAdornment>
                      ),
                    }}
                    placeholder="Enter offset value"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="small"
                    onClick={formik.handleSubmit}
                  >
                    Save Provider Offset
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Card>
      </Stack>

      <Snackbar
        open={showFeedback}
        autoHideDuration={3000}
        onClose={() => setShowFeedback(false)}
        message={snackbarMessage}
      />
    </div>
  );
};

export default UserCard;
